import type { MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";
import { useEffect, useMemo } from "react";
import { Provider, useDispatch } from "react-redux";
import { Outlet, useLoaderData } from "react-router-dom";

import { useStatTrackingModeInitializer } from "@/pages/Tagging/hooks/useStatTrackingModeInitializer";

import {
  MagnusSocketStatus,
  SocketProvider,
} from "../Tagging/components/SocketBroker";
import { setSessionType } from "../Tagging/store/slices/session";
import {
  setCurrentFilter,
  setTrackingState,
} from "../Tagging/store/slices/socket";
import { setActiveView } from "../Tagging/store/slices/view";
import { createTaggingStore } from "../Tagging/store/store";

type LoaderData = {
  machine: MachinePublicHydrated;
};

/**
 * Initializes Coach Mode state inside the Redux Provider.
 * Sets activeView, filter, and tracking state on mount.
 */
const CoachModeInitializer = ({
  children,
  machine,
}: {
  children: React.ReactNode;
  machine: MachinePublicHydrated;
}) => {
  const dispatch = useDispatch();
  useStatTrackingModeInitializer(machine);

  useEffect(() => {
    dispatch(setActiveView("Coach Mode"));
    dispatch(setCurrentFilter("has_pitch_data"));
    dispatch(setTrackingState(true));
    dispatch(setSessionType("game"));

    return () => {
      dispatch(setCurrentFilter("all"));
    };
  }, [dispatch]);

  return <>{children}</>;
};

/**
 * CoachSession wraps all coach mode routes with:
 * - Redux Provider (isolated tagging store)
 * - Socket Context (WebSocket connection for real-time data)
 * - MagnusSocketStatus (connection indicator in navbar)
 * - Coach Mode state initialization
 */
const CoachSession = () => {
  const { machine } = useLoaderData() as LoaderData;

  const store = useMemo(() => {
    console.log(`CoachSession mounted for machine: ${machine.name}`);
    return createTaggingStore();
  }, [machine.name]);

  return (
    <Provider store={store}>
      <SocketProvider>
        <MagnusSocketStatus />
        <CoachModeInitializer machine={machine}>
          <Outlet />
        </CoachModeInitializer>
      </SocketProvider>
    </Provider>
  );
};

export default CoachSession;
